/**
 * RuleSentence — the opening clause: "On <layers> when <ALL|ANY> of these match".
 * Without `appliesTo` (a feature with no layers — the Automation) it opens on
 * "when".
 * For Media Devoted | Created By Ciro Autiero
 *
 * The controls ARE the words, so the words have to carry like words: foreground
 * colour, medium weight, an icon per clause. Muted grey read as disabled hint
 * text and the sentence disappeared between the chips.
 */

import { FC } from 'react';
import { Filter, Layers } from 'lucide-react';
import type { FlowLayer } from '@trafficpulse/shared';

import { SegmentedPills, type SegmentedPillOption } from '@/components/ui';

import LayerScopeChips from '../../calculation/group/LayerScopeChips';

type MatchOp = 'AND' | 'OR';

/** The boolean operator stays on the chip: it is the vocabulary users already
 *  have, and "ALL" alone forces them to remember which one it maps to. */
const MATCH_OPTIONS: ReadonlyArray<SegmentedPillOption<MatchOp>> = [
  { value: 'AND', label: 'ALL (AND)' },
  { value: 'OR', label: 'ANY (OR)' },
];

const WORD = 'text-[13px] font-semibold text-foreground';

export interface RuleSentenceProps {
  appliesTo?: ReadonlyArray<FlowLayer>;
  op: MatchOp;
  onScopeChange?: (next: ReadonlyArray<FlowLayer>) => void;
  onOpChange: (op: MatchOp) => void;
}

export const RuleSentence: FC<RuleSentenceProps> = ({
  appliesTo,
  op,
  onScopeChange,
  onOpChange,
}) => (
  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
    {appliesTo && onScopeChange ? (
      <>
        <span className={`inline-flex items-center gap-1.5 ${WORD}`}>
          <Layers className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          On
        </span>
        <LayerScopeChips value={appliesTo} onChange={onScopeChange} hideLabel />
      </>
    ) : null}
    <span className={`inline-flex items-center gap-1.5 ${WORD}`}>
      <Filter className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      when
    </span>
    <SegmentedPills options={MATCH_OPTIONS} value={op} onChange={onOpChange} />
    <span className={`${WORD} text-muted-foreground`}>of these match</span>
  </div>
);

export default RuleSentence;
